import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthBootstrap } from "../../context/AuthBootstrapContext";
import TypicalAvailabilityPicker from "../OnboardingFlow/TypicalAvailabilityPicker";
import ladleIcon from "../../assets/icons/ladle.png";
import "./index.css";
import CoffeeInviteModal from "../components/CoffeeInviteModal";


const API_BASE = "https://api-2026.fikacreatives.co";
const CITY_PARAMS = { LA: "Los Angeles", NYC: "New York" };

async function readJsonSafely(r) {
  const ct = r.headers.get("content-type") || "";
  if (ct.includes("application/json")) return await r.json();
  const text = await r.text();
  return { ok: false, error: `Expected JSON but got ${ct || "unknown"}: ${text.slice(0, 160)}` };
}

function normalizeFeaturedProfiles(rawProfiles) {
  const arr = Array.isArray(rawProfiles) ? rawProfiles : [];
  return arr.map((profile) => ({
    id: profile?.id || profile?.person_id,
    first_name: profile?.first_name || "",
    last_name: profile?.last_name || "",
    full_name:
      profile?.full_name ||
      [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
      "Featured creative",
    neighborhood: profile?.neighborhood || "",
    creative_type:
      profile?.creative_type ||
      profile?.primary_specialty ||
      profile?.creative_specialty1 ||
      "",
    creative_specialties: Array.isArray(profile?.creative_specialties) && profile.creative_specialties.length
      ? profile.creative_specialties
      : [
          profile?.creative_specialty1 || profile?.creative_type || profile?.primary_specialty,
          profile?.creative_specialty2,
          profile?.creative_specialty3,
        ].filter(Boolean),
    bio: profile?.bio || "",
    featured_blurb: profile?.featured_blurb || profile?.bio || "",
    profile_image_url:
      profile?.profile_image_url || profile?.profile_pic_url || profile?.avatar_url || "",
    cover_image_url:
      profile?.cover_image_url ||
      profile?.featured_image_url ||
      profile?.primary_project_image_url ||
      profile?.project_image_url ||
      profile?.profile_image_url ||
      profile?.profile_pic_url ||
      "",
    saves_count: Number(profile?.saves_count || 0),
    profile_url: profile?.profile_url || `/profile/${profile?.id || profile?.person_id}`,
    status: profile?.status || "",
    subscription_status: (profile?.subscription_status || "").toLowerCase(),
    coffee_invites_enabled: Boolean(profile?.coffee_invites_enabled),
  }));
}

function shouldBlurProfiles(person, verification) {
  if (!person) return false;
  const subStatus = (verification?.subscription_status || "").toLowerCase();
  const status = (person.status || "").toUpperCase();

  // Explicitly marked expired by backend
  if (subStatus === "expired") return true;

  // APPLICATION_COMPLETE on free plan: blur 14 days after signup
  if (status === "APPLICATION_COMPLETE" && subStatus === "free") {
    if (!person.created_at) return true;
    const expiresAt = new Date(person.created_at);
    expiresAt.setDate(expiresAt.getDate() + 14);
    return Date.now() > expiresAt.getTime();
  }

  // PAYMENT_COMPLETE on trialing: blur once trial period ends
  if (status === "PAYMENT_COMPLETE" && subStatus === "trialing") {
    const trialEnd = verification?.current_period_end || verification?.trial_end;
    if (trialEnd) return new Date(trialEnd) < new Date();
  }

  return false;
}

async function goToMembership(person) {
  let sessionId = person?.apply_session_id || null;

  if (!sessionId) {
    try {
      const res = await fetch(`${API_BASE}/api/me/apply-session`, { credentials: "include" });
      const data = await res.json();
      sessionId = data?.apply_session_id || null;
    } catch {}
  }

  const params = new URLSearchParams({ no_trial: "true" });
  if (sessionId) params.set("apply_session_id", sessionId);
  window.location.href = `https://www.fikacreatives.co/apply/pay?${params.toString()}`;
}

const ACTIVE_SUB_STATUSES = ["free", "trialing", "active"];

function FeaturedCard({ profile, canInvite, onInvite }) {
  const allSpecialties = (profile.creative_specialties?.length
    ? profile.creative_specialties
    : [profile.creative_type]
  ).filter(Boolean);

  const pillsRef = useRef(null);
  const [show2, setShow2] = useState(false);

  useLayoutEffect(() => {
    const el = pillsRef.current;
    if (el && el.scrollWidth > el.offsetWidth) setShow2(true);
  }, []);

  const visibleSpecialties = show2 ? allSpecialties.slice(0, 2) : allSpecialties;

  return (
    <Link
      to={profile.profile_url}
      style={{
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        color: "inherit",
        borderRadius: 24,
        overflow: "hidden",
        background: "#fff",
        boxShadow: "0 6px 28px rgba(20,20,20,0.08)",
      }}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: "0.92 / 1",
          background: "#ece9e2",
          overflow: "hidden",
        }}
      >
        {profile.cover_image_url ? (
          <img
            src={profile.cover_image_url}
            alt={profile.full_name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", padding: "0 20px", textAlign: "center" }}>
            <p style={{ fontSize: 13, color: "#7B7067", lineHeight: 1.5 }}>
              Favorite work hasn't been uploaded yet. Check their portfolio to view work!
            </p>
          </div>
        )}

        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, rgba(20,20,20,0.58) 0%, rgba(20,20,20,0.22) 28%, rgba(20,20,20,0.02) 52%, rgba(20,20,20,0) 72%)",
          }}
        />

        {profile.neighborhood && (
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              borderRadius: 999,
              padding: "5px 10px",
              fontSize: 11,
              fontWeight: 500,
              color: "#fff",
              lineHeight: 1,
              pointerEvents: "none",
            }}
          >
            📍 {profile.neighborhood}
          </div>
        )}

        {/* Cup icon badge — shown when profile has an active subscription */}
        {ACTIVE_SUB_STATUSES.includes(profile.subscription_status) && (
          <button
            type="button"
            aria-label={canInvite ? `Invite ${profile.first_name} to coffee` : "Coffee invite"}
            onClick={canInvite ? (e) => { e.preventDefault(); e.stopPropagation(); onInvite(); } : undefined}
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 17,
              border: "none",
              padding: 0,
              cursor: canInvite ? "pointer" : "default",
            }}
          >
            ☕
          </button>
        )}

        <div
          style={{
            position: "absolute",
            left: 18,
            right: 18,
            bottom: 16,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ minWidth: 0, width: "100%" }}>
            {/* Avatar + name row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                {profile.profile_image_url ? (
                  <img
                    src={profile.profile_image_url}
                    alt={profile.full_name}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid rgba(255,255,255,0.75)",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.25)",
                      border: "2px solid rgba(255,255,255,0.75)",
                      flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ color: "#fff", fontSize: 18, fontWeight: 600, lineHeight: 1.1 }}>
                  {profile.full_name}
                </div>
              </div>

              {profile.saves_count >= 1 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 500,
                    flexShrink: 0,
                  }}
                >
                  <span aria-hidden="true">☕</span>
                  <span>{profile.saves_count}</span>
                </div>
              )}
            </div>

            {/* Glass specialty pills */}
            <div ref={pillsRef} style={{ display: "flex", flexWrap: "nowrap", gap: 6, overflow: "hidden" }}>
              {visibleSpecialties.map((s) => (
                <span
                  key={s}
                  style={{
                    display: "inline-block",
                    padding: "5px 12px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 500,
                    color: "#fff",
                    background: "rgba(255,255,255,0.18)",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    border: "none",
                    lineHeight: 1.3,
                    whiteSpace: "nowrap",
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          padding: "14px 18px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          background: "#fff",
        }}
      >
        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.45, color: "#262626" }}>
          {profile.featured_blurb || "View profile"}
        </p>
      </div>
    </Link>
  );
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function detectCity(person) {
  const c = (person?.city || "").toLowerCase();
  if (c.includes("new york") || c.includes("nyc")) return "NYC";
  return "LA";
}

export default function FeaturedPage() {
  const { person, profileAssets, verification, loading: authLoading } = useAuthBootstrap();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [city, setCity] = useState(null);
  const [feedTab, setFeedTab] = useState("city");
  const [search, setSearch] = useState("");
  const [isStirring, setIsStirring] = useState(false);
  const [displayProfiles, setDisplayProfiles] = useState(null);
  const [availModalDismissed, setAvailModalDismissed] = useState(false);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteSharePhone, setInviteSharePhone] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const viewerCanInvite = Boolean(
    person?.id &&
    ACTIVE_SUB_STATUSES.includes((verification?.subscription_status || "").toLowerCase())
  );

  async function handleInvite(e) {
    e.preventDefault();
    setInviteSending(true);
    setInviteError("");
    try {
      const res = await fetch(`${API_BASE}/api/coffee-invites`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          inviteeId: inviteTarget?.id,
          senderPersonId: person?.id,
          senderFirstName: person?.first_name || "",
          senderLastName: person?.last_name || "",
          message: inviteMessage.trim(),
          sharePhone: inviteSharePhone,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send invite");
      setInviteSent(true);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviteSending(false);
    }
  }

  function closeInviteModal() {
    setInviteTarget(null);
    setInviteMessage("");
    setInviteSharePhone(false);
    setInviteError("");
    setInviteSent(false);
  }

  // Set city only after auth resolves so we don't default to LA for NYC users
  useEffect(() => {
    if (!authLoading) setCity(detectCity(person));
  }, [authLoading, person]);

  const userCity = detectCity(person);

  // Viewer's own specialties for "Similar Creatives" matching
  const viewerSpecialties = [
    person?.creative_specialty1,
    person?.creative_specialty2,
    person?.creative_specialty3,
  ].filter(Boolean).map((s) => s.toLowerCase());

  const similarProfiles = profiles.filter((p) => {
    if (!viewerSpecialties.length) return false;
    return (p.creative_specialties || []).some((s) =>
      viewerSpecialties.includes((s || "").toLowerCase())
    );
  });

  const hasTypicalAvailability = (() => {
    const v = person?.typical_availability ?? profileAssets?.typical_availability;
    if (!v) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim().length > 0;
    return true;
  })();

  const showAvailModal = !hasTypicalAvailability && !availModalDismissed;

  const loadProfiles = useCallback(async (selectedCity) => {
    setLoading(true);
    setError("");
    try {
      const cityParam = CITY_PARAMS[selectedCity] || CITY_PARAMS.LA;
      const r = await fetch(
        `${API_BASE}/api/featured-profiles?city=${encodeURIComponent(cityParam)}`,
        { method: "GET", credentials: "include" }
      );
      const data = await readJsonSafely(r);
      if (!r.ok || !data?.ok) {
        setError(data?.error || "Failed to load featured creatives.");
        return;
      }
      setProfiles(normalizeFeaturedProfiles(data.profiles || []));
    } catch {
      setError("Network error loading featured creatives.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (city === null) return;
    loadProfiles(city);
  }, [loadProfiles, city]);

  const blurred = shouldBlurProfiles(person, verification);

  const filteredProfiles = profiles.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (p.creative_type || "").toLowerCase().includes(q);
  });

  // Reset display order when filters change
  useEffect(() => {
    setDisplayProfiles(null);
  }, [search, city, profiles]);

  function handleStir() {
    if (isStirring) return;
    setIsStirring(true);
    setDisplayProfiles(shuffle(displayProfiles ?? filteredProfiles));
    setTimeout(() => setIsStirring(false), 900);
  }

  const cityProfiles = displayProfiles ?? filteredProfiles;
  const shownProfiles = feedTab === "similar" ? similarProfiles : cityProfiles;

  if (loading) {
    return <div className="featuredPage"><div className="featuredLoading">Loading featured creatives…</div></div>;
  }

  if (error) {
    return <div className="featuredPage"><div className="featuredError">{error}</div></div>;
  }

  const visibleProfiles = blurred ? shownProfiles.slice(0, 2) : shownProfiles;

  return (
    <>
    {showAvailModal && (
      <div className="availModal__overlay">
        <div className="availModal__card">
          <button
            type="button"
            className="availModal__close"
            onClick={() => setAvailModalDismissed(true)}
            aria-label="Dismiss"
          >
            ×
          </button>
          <div className="availModal__header">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <h2 className="availModal__title" style={{ margin: 0 }}>When are you typically free to meet 2-3 creatives?</h2>
              <span className="availModal__newPill">New</span>
            </div>
            <p className="availModal__subtitle">
            Whether you're usually working from cafe or set a time to meet other creatives, when works best in your schedule? Tell us your usual availability, so we can find the best Fika matches for you. Based on your schedule, we'll send a time/place for you to connect. (Time selection optimized for web)
            </p>
          </div>
          <TypicalAvailabilityPicker onSubmit={() => setAvailModalDismissed(true)} />
        </div>
      </div>
    )}
    <div className="featuredPage">
      <h2 style={{ fontFamily: "Clearface", fontSize: 22, margin: 8, fontWeight: 400 }}>
        Meet the inspiring creative community around you.
      </h2>
      <p style={{ color: "#888", fontSize: 14, margin: "4px 8px 8px" }}>Discover people building interesting things, explore their work, and start a coffee conversation when someone sparks your curiosity.Invites expire after 24 hours.</p>

      {/* Controls row: tabs + stir + search all in one line */}
      <div className="featuredControls">
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div className="featuredCityToggle">
            <button
              type="button"
              className={`featuredCityBtn${feedTab === "city" ? " isActive" : ""}`}
              onClick={() => setFeedTab("city")}
            >
              {userCity}
            </button>
            {userCity !== "NYC" && (
              <button
                type="button"
                className={`featuredCityBtn${feedTab === "similar" ? " isActive" : ""}`}
                onClick={() => setFeedTab("similar")}
              >
                Similar Creatives
              </button>
            )}
          </div>

          <button
            type="button"
            className={`stirBtn${isStirring ? " stirBtn--active" : ""}`}
            onClick={handleStir}
            disabled={isStirring || feedTab === "similar"}
            title="Shuffle creatives"
          >
            <img
              src={ladleIcon}
              alt=""
              aria-hidden="true"
              className={`stirIcon${isStirring ? " stirIcon--spinning" : ""}`}
              style={{ width: 16, height: 16, objectFit: "contain" }}
            />
            Stir it Up
          </button>
        </div>

        <input
          className="featuredSearch"
          type="text"
          placeholder="Search by creative type..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search by creative type"
        />
      </div>

      <section aria-label="Featured creatives" style={{ position: "relative" }}>
        {visibleProfiles.length === 0 && !blurred ? (
          <div className="featuredLoading" style={{ maxWidth: 400, margin: "48px auto", textAlign: "center", lineHeight: 1.6 }}>
            {feedTab === "similar"
              ? "The Fika community is growing. Check back in for creatives like you, or get inspired by creatives on the feed."
              : search.trim() ? "No creatives found for that search. Try again." : "No featured creatives in this city yet."}
          </div>
        ) : (
        <div
          className="featuredGrid"
          style={{
            columnCount: 2,
            columnGap: 22,
            filter: blurred ? "blur(6px)" : "none",
            pointerEvents: blurred ? "none" : "auto",
            userSelect: blurred ? "none" : "auto",
          }}
        >
          {visibleProfiles.map((profile) => (
            <div
              key={profile.id}
              className={isStirring ? "featuredItem--scramble" : ""}
              style={{ breakInside: "avoid", marginBottom: 22 }}
            >
              <FeaturedCard
                profile={profile}
                canInvite={viewerCanInvite && profile.coffee_invites_enabled && ACTIVE_SUB_STATUSES.includes(profile.subscription_status)}
                onInvite={() => setInviteTarget(profile)}
              />
            </div>
          ))}
        </div>
        )}

        {blurred && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "0 24px",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                padding: "28px 28px",
                boxShadow: "0 8px 32px rgba(20,20,20,0.12)",
                maxWidth: 320,
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 12 }}>☕</div>
              <div
                style={{ fontWeight: 700, fontSize: 17, color: "#1a1a2e", marginBottom: 8, lineHeight: 1.3 }}
              >
                Become a member to access creatives near you
              </div>
              <div
                style={{ fontSize: 14, color: "#7B7067", lineHeight: 1.5, marginBottom: 20 }}
              >
                Your free preview has ended. Join Fika to connect with your creative community.
              </div>
              <button
                type="button"
                onClick={() => goToMembership(person)}
                style={{
                  display: "inline-block",
                  background: "#1a1a2e",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  padding: "12px 24px",
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Become a member
              </button>
            </div>
          </div>
        )}
      </section>
    </div>

    {inviteTarget && (
      <CoffeeInviteModal
        inviteeName={inviteTarget.first_name}
        inviteeFullName={inviteTarget.full_name}
        message={inviteMessage}
        onMessageChange={setInviteMessage}
        sharePhone={inviteSharePhone}
        onSharePhoneChange={setInviteSharePhone}
        sending={inviteSending}
        sent={inviteSent}
        error={inviteError}
        onSubmit={handleInvite}
        onClose={closeInviteModal}
      />
    )}
    </>
  );
}
